import React, { useEffect, useState } from 'react';
import ApiService from '../../../../../services/ApiService';
import {
  Overlay,
  ModalContainer,
  Title,
  Subtitle,
  EmptyState,
  Table,
  Th,
  Td,
  QtyInput,
  Actions,
  OutlineButton,
  PrimaryButton,
} from './style';

interface Props {
  invoiceId: number;
  orderId: number;
  onClose: () => void;
  onAdded: () => void;
  onError: (msg: string) => void;
}

// Modal de adição de itens a uma nota existente (rascunho). Reusa os itens elegíveis do
// pedido (saldo já descontado pelo backend) e chama addInvoiceItems — não cria nota nova.
const AddInvoiceItemsModal: React.FC<Props> = ({ invoiceId, orderId, onClose, onAdded, onError }) => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await ApiService.fiscal.getEligibleNfeItems(orderId);
        setItems((Array.isArray(data) ? data : []).map((i: any) => ({ ...i, selected: false, qtd: '' })));
      } catch {
        onError('Erro ao carregar itens disponíveis do pedido.');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orderId, onClose, onError]);

  const toggle = (idx: number) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, selected: !it.selected } : it)));

  const setQtd = (idx: number, value: string) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, qtd: value } : it)));

  const nome = (it: any) =>
    it.nomeCustomizado || it.Product?.name || it.ploomesProductName || it.Material?.name || `Item ${it.id}`;

  const unitario = (it: any) => Number(it.priceKg || it.valorUnitario || 0);

  const handleConfirm = async () => {
    const selecionados = items.filter((it) => it.selected && Number(it.qtd) > 0);
    if (selecionados.length === 0) {
      onError('Selecione ao menos um item e informe uma quantidade válida.');
      return;
    }
    const payload = selecionados.map((it) => {
      const quantidade = Number(it.qtd);
      const valorUnitario = unitario(it);
      return { orderItemId: it.id, quantidade, valorUnitario, valorTotal: quantidade * valorUnitario };
    });
    setSaving(true);
    try {
      await ApiService.fiscal.addInvoiceItems(invoiceId, payload);
      onAdded();
    } catch (error: any) {
      onError(error?.response?.data?.message || 'Erro ao adicionar itens à nota.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <Title>Adicionar itens do pedido</Title>
        <Subtitle>Somente itens com saldo disponível no pedido {orderId}.</Subtitle>

        {loading ? (
          <EmptyState>Carregando…</EmptyState>
        ) : items.length === 0 ? (
          <EmptyState>Nenhum item disponível para adicionar.</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Adicionar</Th>
                <Th>Item</Th>
                <Th>Disponível</Th>
                <Th>Qtd</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={it.id}>
                  <Td>
                    <input type="checkbox" checked={it.selected} onChange={() => toggle(idx)} />
                  </Td>
                  <Td>{nome(it)}</Td>
                  <Td>{it.quantidadeDisponivel}</Td>
                  <Td>
                    <QtyInput
                      type="number"
                      min={1}
                      max={it.quantidadeDisponivel}
                      value={it.selected ? it.qtd : ''}
                      disabled={!it.selected}
                      onChange={(e) => setQtd(idx, e.target.value)}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <Actions>
          <OutlineButton onClick={onClose} disabled={saving}>Cancelar</OutlineButton>
          <PrimaryButton onClick={handleConfirm} disabled={saving || loading}>
            {saving ? 'Adicionando…' : 'Adicionar'}
          </PrimaryButton>
        </Actions>
      </ModalContainer>
    </Overlay>
  );
};

export default AddInvoiceItemsModal;
